<template>
  <Popup
    :instance-id="instanceId"
    module-name="CountdownPopup"
    class="countdown-popup"
    data-module="countdown-popup"
    :trigger-type="triggerType"
    :delay-to-show="delayToShow"
    :show-on-desk="showOnDesk"
    :show-on-mobile="showOnMobile"
    :style="{
      zIndex,
      '--text-color': textColor,
      '--background-color': backgroundColor,
      '--cta-color': ctaColor,
      '--button-text-color': buttonTextColor,
      '--title-weight': titleWeight,
      '--color-counter': timerColor,
      '--background-counter': backgroundCounter,
    }"
  >
    <div class="countdown-popup__container">
      <div
        v-if="image"
        class="countdown-popup__container__img"
      >
        <LImage
          class="img"
          :src="image"
          :alt="imageAlt"
        />
      </div>
      <div class="countdown-popup__container__text">
        <header v-if="title || text">
          <h2 v-if="title">
            {{ title }}
          </h2>
          <div
            v-if="text"
            class="txt"
            v-html="text"
          ></div>
        </header>
        <div class="countdown-popup__container__countdown__timer">
          <div class="timer">
            <div class="time-unit">
              <div class="time-value">
                {{ remainingTime.days }}
              </div>
              <div class="time-label">
                {{ $tc('days', remainingTime.days) }}
              </div>
            </div>
            <div class="time-unit">
              <div class="time-value">
                {{ remainingTime.hours }}
              </div>
              <div class="time-label">
                {{ $tc('hours', remainingTime.hours) }}
              </div>
            </div>
            <div class="time-unit">
              <div class="time-value">
                {{ remainingTime.minutes }}
              </div>
              <div class="time-label">
                {{ $tc('minutes', remainingTime.minutes) }}
              </div>
            </div>
            <div class="time-unit">
              <div class="time-value">
                {{ remainingTime.seconds }}
              </div>
              <div class="time-label">
                {{ $tc('seconds', remainingTime.seconds) }}
              </div>
            </div>
          </div>
          <EditorLink
            v-if="linkLabel"
            class="cta"
            :model="linkUrl"
          >
            {{ linkLabel }}
          </EditorLink>
        </div>
      </div>
    </div>
  </Popup>
</template>

<script>
import weightOptions from '@/helpers/weightOptions.json';
import Popup from '../generic/modals/Popup.vue';
import EditorLink from '../EditorLink.vue';

export default {
  name: 'CountdownPopup',
  components: {
    Popup,
    EditorLink,
  },
  inheritAttrs: false,
  props: {
    ...Popup.props,
    instanceId: {
      type: String,
      default: '',
    },
    title: {
      type: String,
      default: '',
    },
    text: {
      type: String,
      default: '',
    },
    imageDesk: {
      type: String,
      default: '',
    },
    useDifferentMobileBgImage: {
      type: Boolean,
      default: false,
    },
    date: {
      type: String,
      default: '',
    },
    imageMobile: {
      type: String,
      default: '',
    },
    imageDeskAlt: {
      type: String,
      default: '',
    },
    imageMobileAlt: {
      type: String,
      default: '',
    },
    linkLabel: {
      type: String,
      default: '',
    },
    linkUrl: {
      type: Object,
      default: () => ({
        type: 'externalLink',
        value: '',
        openInNewTab: true,
      }),
    },
    textColor: {
      type: String,
      default: '',
    },
    titleWeight: {
      type: String,
      default: '600',
    },
    ctaColor: {
      type: String,
      default: '',
    },
    timerColor: {
      type: String,
      default: '',
    },
    buttonTextColor: {
      type: String,
      default: '',
    },
    backgroundColor: {
      type: String,
      default: '#FFFFFF',
    },
    backgroundCounter: {
      type: String,
      default: '#FFFFFF',
    },
  },
  data() {
    return {
      now: new Date(),
      interval: null,
      isDesktop: true,
    };
  },
  computed: {
    targetDate() {
      return new Date(this.date);
    },
    image() {
      if (this.isDesktop || !this.useDifferentMobileBgImage) {
        return this.imageDesk;
      }
      return this.imageMobile;
    },
    imageAlt() {
      if (
        this.isDesktop ||
        !this.useDifferentMobileBgImage ||
        !this.imageMobileAlt
      ) {
        return this.imageDeskAlt;
      }
      return this.imageMobileAlt;
    },
    timeIsUp() {
      return this.now >= this.targetDate;
    },
    remainingTime() {
      const remainingTimeInSecs = Math.floor(
        (this.targetDate - this.now) / 1000,
      );

      if (remainingTimeInSecs < 0) {
        return {
          seconds: 0,
          minutes: 0,
          hours: 0,
          days: 0,
        };
      }

      return {
        seconds: remainingTimeInSecs % 60,
        minutes: Math.floor(remainingTimeInSecs / 60) % 60,
        hours: Math.floor(remainingTimeInSecs / 60 / 60) % 24,
        days: Math.floor(remainingTimeInSecs / 60 / 60 / 24),
      };
    },
  },
  watch: {
    date() {
      this.start();
    },
  },
  mounted() {
    this.start();
    this.isDesktop = window.matchMedia('(min-width: 900px)').matches;
    window.addEventListener('resize', () => {
      this.isDesktop = window.matchMedia('(min-width: 900px)').matches;
    });
  },

  beforeDestroy() {
    clearInterval(this.interval);
  },
  methods: {
    start() {
      this.stop();

      if (!this.timeIsUp) {
        this.interval = window.setInterval(() => {
          this.now = new Date();

          if (this.timeIsUp) {
            this.stop();
          }
        }, 1000);
      }
    },
    stop() {
      window.clearInterval(this.interval);
    },
  },
  metadata: {
    extends: Popup.metadata,
    tabs: [
      {
        name: 'content',
        groups: [
          {
            name: 'text',
            label: 'Conteúdo',
            position: 0,
            fields: [
              {
                name: 'date',
                type: 'DatetimePicker',
                label: 'Data final do cronômetro',
                enableTime: true,
                isClearable: false,
              },
              {
                name: 'title',
                type: 'ShortText',
                label: 'Título',
              },
              {
                name: 'titleWeight',
                type: 'VueSelect',
                label: 'Peso do título',
                default: '300',
                options: weightOptions,
                isEnabled({ title }) {
                  return title;
                },
              },
              {
                name: 'text',
                label: 'Texto de apoio',
                type: 'Html',
                maxlength: 300,
              },
              {
                name: 'linkLabel',
                label: 'Texto do botão',
                type: 'ShortText',
                maxlength: 35,
              },
              {
                name: 'linkUrl',
                label: 'URL do link',
                type: 'Url',
              },
            ],
          },
          {
            name: 'image',
            label: 'Imagem',
            after: 'trigger',
            fields: [
              {
                name: 'imageDesk',
                type: 'ImageFile',
                label: 'Imagem',
                helpText: 'Dimensões recomendadas: 720x202',
                enableRemoval: true,
              },
              {
                name: 'imageDeskAlt',
                type: 'ShortText',
                label: 'Texto alternativo para a imagem',
              },
              {
                name: 'useDifferentMobileBgImage',
                type: 'Checkbox',
                label: 'Usar uma imagem diferente para mobile',
              },
              {
                name: 'imageMobile',
                type: 'ImageFile',
                label: 'Imagem (mobile)',
                helpText: 'Dimensões recomendadas: 500x202',
                enableRemoval: true,
                isEnabled({ useDifferentMobileBgImage }) {
                  return useDifferentMobileBgImage;
                },
              },
              {
                name: 'imageMobileAlt',
                type: 'ShortText',
                label: 'Texto alternativo para a imagem mobile',
                isEnabled({ useDifferentMobileBgImage }) {
                  return useDifferentMobileBgImage;
                },
              },
            ],
          },
        ],
      },
      {
        name: 'appearance',
        label: 'Aparência',
        groups: [
          {
            name: 'colors',
            label: 'Cores',
            fields: [
              {
                name: 'backgroundColor',
                type: 'ColorPicker',
                label: 'Cor de fundo',
              },
              {
                name: 'textColor',
                type: 'ColorPicker',
                label: 'Cor do texto',
              },
              {
                name: 'ctaColor',
                type: 'ColorPicker',
                label: 'Cor do botão',
              },
              {
                name: 'buttonTextColor',
                type: 'ColorPicker',
                label: 'Cor de texto do botão',
              },
              {
                name: 'timerColor',
                type: 'ColorPicker',
                label: 'Cor de texto do cronômetro',
              },
              {
                name: 'backgroundCounter',
                type: 'ColorPicker',
                label: 'Cor de fundo do cronômetro',
              },
            ],
          },
        ],
      },
    ],
  },
  i18n: {
    messages: {
      pt: {
        days: 'Dia | Dia | Dias',
        hours: 'Hora | Hora | Horas',
        minutes: 'Minuto | Minuto | Minutos',
        seconds: 'Segundo | Segundo | Segundos',
      },
      en: {
        days: 'Day | Day | Days',
        hours: 'Minute | Minute | Minutes',
        minutes: 'Hour | Hour | Hours',
        seconds: 'Second | Second | Seconds',
      },
      es: {
        days: 'Dia | Dia | Dias',
        hours: 'Hora | Hora | Horas',
        minutes: 'Minuto | Minuto | Minutos',
        seconds: 'Segundo | Segundo | Segundos',
      },
    },
  },
};
</script>

<style scoped lang="scss">
.countdown-popup {
  &::v-deep .popup__wrapper {
    > i {
      border-radius: 20px;
    }
  }

  &__container {
    text-align: center;
    margin: 0 auto;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  &__container__img {
    .img {
      object-fit: cover;
      max-height: 202px;
      display: block;
    }
  }

  &__container__text {
    padding: 34px 20px;
    background-color: var(--background-color);

    &:only-child {
      padding: 60px 20px;
    }

    header {
      margin-bottom: 24px;
    }

    h2 {
      color: var(--text-color);
      font-weight: var(--title-weight, 600);

      @include media-breakpoint-up(md) {
        font-size: 32px;
      }
    }

    .txt {
      padding-top: 16px;
      color: var(--text-color);
    }
  }

  &__container__countdown__timer {
    display: flex;
    flex-direction: column;
    align-items: center;

    .timer {
      display: flex;
      justify-content: center;
    }

    .time-unit {
      display: flex;
      flex-direction: column;
      align-items: center;

      &:not(:first-child) {
        margin-left: 10px;
      }
    }

    .time-value {
      font-size: 2em;
      color: var(--color-counter);
      border: 0.8px solid var(--color-counter);
      padding: 8px;
      border-radius: 8px;
      min-width: 60px;
      background-color: var(--background-counter);
      font-weight: 500;

      @include media-breakpoint-up(md) {
        font-size: 3em;
        min-width: 80px;
      }
    }

    .time-label {
      font-size: 0.8em;
      color: var(--text-color);
      margin-top: 5px;

      @include media-breakpoint-up(md) {
        font-size: 0.9em;
      }
    }

    .cta {
      display: block;
      font-weight: 500;
      min-height: unset;
      font-weight: 500;
      font-size: $size-small;
      background-color: var(--cta-color);
      color: var(--button-text-color);
      margin-top: 20px;
      text-transform: uppercase;
      overflow: hidden;
      border-radius: 50px;
      width: 100%;
      max-width: 356px;
      padding: 17px 30px;

      @include media-breakpoint-up(md) {
        font-size: $size-normal;
      }
    }
  }
}
</style>
